import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useDashboardConfig } from '@/hooks/useDashboardConfig';
import { useHasFilter } from '@/hooks/useHasFilter';

import {
  IButtonField,
  parseAllParameters,
  TUseFiltersdReturn,
  ISearch,
  ISearchField,
} from '@/ui-kit/table-filters/filters.type';
import { IFilterBeneficiary } from '@/features/beneficiaries/store/models/beneficiary.model';

export interface IAllBeneficiariesFiltersFormFields {
  type?: string;
  search?: ISearch;
}

export type TAllBeneficiariesFields = { type: IButtonField; search: ISearchField };

export type TAllBeneficiariesFiltersKeys = keyof IAllBeneficiariesFiltersFormFields;

export function parseAllBeneficiariesFiltersValue(
  type: TAllBeneficiariesFiltersKeys,
  value: IAllBeneficiariesFiltersFormFields[TAllBeneficiariesFiltersKeys],
) {
  if (type === 'type') {
    if (value === null || value === undefined) return {};
    return { usableForSct: value === 'scte' ? true : false };
  }
  if (type === 'search') {
    const searchValue = value as ISearch;
    if (!searchValue || searchValue.value === '') return {};
    if (searchValue.key === 'name') {
      const key = `${(value as ISearch).value.trim().replace(/\s+/g, '%')}`;
      return {
        filter: `name like %${key}%`,
      };
    }
    return { [searchValue.key]: searchValue.value };
  }
}

export function useAllBeneficiariesFilter(
  onChange: (queryParameters) => void,
  formDefaultValues: IAllBeneficiariesFiltersFormFields,
): TUseFiltersdReturn<TAllBeneficiariesFields> {
  const config = useDashboardConfig();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const typeOptions =
    config.settings.beneficiaries?.map((beneficiaryType) => ({
      value: beneficiaryType,
      label: t(`pages.currentProfiles.beneficiaries.tabs.${beneficiaryType}`),
    })) ?? [];

  const defaultType = typeOptions?.[0]?.value;

  const defaultValues: IAllBeneficiariesFiltersFormFields = { type: defaultType, ...formDefaultValues };

  const methods = useForm<IAllBeneficiariesFiltersFormFields>({ defaultValues });

  const getQueryParameters = useCallback(() => {
    const values = methods.getValues();
    return parseAllParameters<IAllBeneficiariesFiltersFormFields, IFilterBeneficiary>(
      values,
      parseAllBeneficiariesFiltersValue,
    );
  }, [methods]);

  const handleClear = useCallback(() => {
    methods.reset({ type: defaultType, search: null });
    onChange(getQueryParameters());
  }, [methods, onChange, getQueryParameters, defaultType]);

  const onUpdateFilter = useCallback(
    (type: TAllBeneficiariesFiltersKeys, value: keyof IAllBeneficiariesFiltersFormFields) => {
      if (searchParams?.size > 0) {
        setSearchParams('');
      }
      methods.setValue(type, value);
      onChange(getQueryParameters());
    },
    [getQueryParameters, methods, onChange, searchParams, setSearchParams],
  );

  const filtersDeps = JSON.stringify(formDefaultValues || {});

  useEffect(() => {
    const values = JSON.parse(filtersDeps);
    Object.keys(values).map((key: keyof IAllBeneficiariesFiltersFormFields) => {
      methods.setValue(key, values[key]);
    });
  }, [filtersDeps, methods]);

  const searchFilterOptions = [
    { label: t('pages.currentProfiles.beneficiaries.filters.id'), value: 'id' },
    { label: t('pages.currentProfiles.beneficiaries.tables.iban'), value: 'IbanInClear' },
    { label: t('pages.currentProfiles.beneficiaries.tables.bic'), value: 'bic' },
    { label: t('pages.currentProfiles.beneficiaries.filters.name'), value: 'name' },
  ];

  const type = {
    value: methods.watch('type'),
    handleChange: (value) => onUpdateFilter('type', value),
    values: typeOptions,
  };
  const search = {
    value: methods.watch('search'),
    handleChange: (value) => onUpdateFilter('search', value),
    options: searchFilterOptions,
  };

  const hasFilters = useHasFilter({ type, search });

  return { type, search, clearFilters: handleClear, hasFilters };
}
